/**
 * Client-side support-ticket notifications store.
 *
 * Why this exists:
 *   The platform `notifications` API (tenant-mgmt-backed) handles billing /
 *   access-request lifecycle events. New-message events on a user's own
 *   support tickets are detected client-side by polling `GET /support/tickets/mine`
 *   and comparing each ticket's `updated_at` against a per-ticket
 *   `lastSeen` mark persisted in localStorage. We keep those entries in a
 *   Zustand store so the bell badge + dropdown can render them without a
 *   round-trip to the backend (which doesn't emit a notification row for
 *   this case today).
 *
 *   The store is intentionally NOT persisted — it's a derived view of
 *   tickets the user has open right now. The lastSeen map (which DOES
 *   need to survive a reload to avoid re-flashing already-seen messages)
 *   lives in localStorage under `support:lastSeen:{ticketId}`.
 */

import { create } from 'zustand';

export interface SupportNotificationEntry {
  /** Stable id — uses `${ticketId}:${lastMessageAt}` so re-renders dedupe. */
  id: string;
  ticketId: string;
  shortId: string;
  subject: string;
  bodyPreview: string;
  /** Last activity ts (ISO 8601) for the ticket. */
  lastMessageAt: string;
  /** When the local detector first surfaced this entry. */
  detectedAt: number;
  read: boolean;
}

interface SupportNotificationsState {
  entries: SupportNotificationEntry[];
  /** Total of unread entries — derived helper, also stored to keep
   *  Header re-renders cheap (no need to filter on every subscribe). */
  unreadCount: number;

  /**
   * Add (or update) a notification entry. If an entry with the same id
   * exists, it's replaced (keeps the latest preview/timestamp); if a
   * different message for the same ticket arrives, both stay so the
   * dropdown can show a short history.
   */
  upsert: (entry: SupportNotificationEntry) => void;
  /** Mark a single entry as seen — typically when the user clicks through. */
  markRead: (id: string) => void;
  /** Mark every entry as seen — triggered by opening the bell dropdown. */
  markAllRead: () => void;
  /** Drop a single entry (e.g. dismiss button). */
  remove: (id: string) => void;
  /** Wipe everything — used when feature flag flips off or user logs out. */
  clear: () => void;
}

const computeUnread = (entries: SupportNotificationEntry[]): number =>
  entries.reduce((acc, e) => (e.read ? acc : acc + 1), 0);

export const useSupportNotificationsStore = create<SupportNotificationsState>((set) => ({
  entries: [],
  unreadCount: 0,

  upsert: (entry) =>
    set((state) => {
      const existing = state.entries.find((e) => e.id === entry.id);
      let next: SupportNotificationEntry[];
      if (existing) {
        // Preserve `read` flag if the entry was already seen — re-receiving
        // it from the poller shouldn't un-mark it.
        next = state.entries.map((e) =>
          e.id === entry.id ? { ...entry, read: e.read } : e,
        );
      } else {
        // Cap at 25 to keep the dropdown render bounded.
        next = [entry, ...state.entries].slice(0, 25);
      }
      return { entries: next, unreadCount: computeUnread(next) };
    }),

  markRead: (id) =>
    set((state) => {
      const next = state.entries.map((e) => (e.id === id ? { ...e, read: true } : e));
      return { entries: next, unreadCount: computeUnread(next) };
    }),

  markAllRead: () =>
    set((state) => {
      const next = state.entries.map((e) => ({ ...e, read: true }));
      return { entries: next, unreadCount: 0 };
    }),

  remove: (id) =>
    set((state) => {
      const next = state.entries.filter((e) => e.id !== id);
      return { entries: next, unreadCount: computeUnread(next) };
    }),

  clear: () => set({ entries: [], unreadCount: 0 }),
}));

/* ─── localStorage helpers ─────────────────────────────────────────────── */

const LS_PREFIX = 'support:lastSeen:';

/**
 * Returns the persisted `lastSeen` timestamp for a ticket as an epoch ms
 * value, or 0 if we've never seen the ticket before (so any update counts
 * as new).
 */
export function getLastSeen(ticketId: string): number {
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + ticketId);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Persist `ts` (epoch ms) as the user's lastSeen mark for the ticket.
 * Failures (e.g. private-browsing quota) are swallowed — the worst case
 * is one extra re-flash of the entry on reload.
 */
export function setLastSeen(ticketId: string, ts: number): void {
  try {
    window.localStorage.setItem(LS_PREFIX + ticketId, String(ts));
  } catch {
    /* no-op */
  }
}
